import React, { useState, useRef } from 'react';
import './TextBox.css';

const TextBox = ({ id, content, position, styles, onUpdate, onClick, isSelected }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const textBoxRef = useRef(null);

  const handleMouseDown = (e) => {
    if (!e.target.closest('.resize-handle') && !isEditing) {
      e.preventDefault(); // Prevent text selection while dragging
      setIsDragging(true);
      
      // Calculate offset from the current position to mouse position
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      // Calculate new position based on mouse position and initial offset
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Snap to grid (20px grid)
      const gridSize = 20;
      const snappedX = Math.round(newX / gridSize) * gridSize;
      const snappedY = Math.round(newY / gridSize) * gridSize;
      
      onUpdate(id, {
        position: {
          x: snappedX,
          y: snappedY
        }
      });
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = (e) => {
    setIsEditing(false);
    onUpdate(id, { content: e.target.innerText });
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const rect = textBoxRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.width,
      y: e.clientY - rect.height
    });
  };

  const handleResizeMouseMove = (e) => {
    if (isResizing) {
      const newWidth = Math.max(100, e.clientX - dragOffset.x);
      const newHeight = Math.max(50, e.clientY - dragOffset.y);
      
      // Snap sizes to grid
      const gridSize = 20;
      const snappedWidth = Math.round(newWidth / gridSize) * gridSize;
      const snappedHeight = Math.round(newHeight / gridSize) * gridSize;
      
      onUpdate(id, {
        styles: {
          ...styles,
          width: `${snappedWidth}px`,
          height: `${snappedHeight}px`
        }
      });
    }
  };

  const handleResizeMouseUp = () => {
    setIsResizing(false);
  };

  const handleBoxClick = (e) => {
    if (!isEditing && !isDragging && !isResizing) {
      onClick();
    }
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMouseMove);
      window.addEventListener('mouseup', handleResizeMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleResizeMouseMove);
      window.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [isDragging, isResizing, dragOffset]); // Added dragOffset to dependencies

  return (
    <div
      ref={textBoxRef}
      className={`text-box ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${isSelected ? 'selected' : ''}`}
      style={{
        left: position.x,
        top: position.y,
        ...styles,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onClick={handleBoxClick}
      onMouseDown={handleMouseDown}
    >
      <div
        className="text-box-content"
        contentEditable={isEditing}
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        suppressContentEditableWarning={true}
      >
        {content}
      </div>
      <div 
        className="resize-handle"
        onMouseDown={handleResizeMouseDown}
      />
    </div>
  );
};

export default TextBox; 